import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation, Link } from 'wouter';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Define the email template schema
const templateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
  type: z.string().min(1, 'Type is required'),
  subject: z.string().min(1, 'Subject is required'),
  textContent: z.string().min(1, 'Text content is required'),
  htmlContent: z.string().min(1, 'HTML content is required'),
  variables: z.array(z.string()).optional(),
});

type TemplateFormValues = z.infer<typeof templateSchema>;

const EditEmailTemplate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const isEditing = id !== 'new';
  const [htmlContent, setHtmlContent] = useState('');

  // Quill editor modules and formats
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'list',
    'bullet',
    'align',
    'link',
    'image',
  ];

  // Form initialization
  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: '',
      description: '',
      type: '',
      subject: '',
      textContent: '',
      htmlContent: '',
      variables: [],
    },
  });

  // Fetch template data if editing
  const {
    data: template,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['/api/email-templates', id],
    queryFn: async () => {
      if (id === 'new') return null;
      const response = await fetch(`/api/email-templates/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch template');
      }
      return response.json();
    },
    enabled: isEditing,
  });

  // Update form values when template data is loaded
  useEffect(() => {
    if (template && isEditing) {
      const htmlContent = template.htmlContent || '';
      setHtmlContent(htmlContent);

      form.reset({
        name: template.name,
        description: template.description,
        type: template.type,
        subject: template.subject,
        textContent: template.textContent,
        htmlContent: htmlContent,
        variables: template.variables || [],
      });
    }
  }, [template, form, isEditing]);

  // Update form when HTML content changes in the editor
  useEffect(() => {
    form.setValue('htmlContent', htmlContent);

    // Extract variables from the content
    const extractVariables = (content: string): string[] => {
      const regex = /\{\{([^{}]+)\}\}/g;
      const matches = content.match(regex) || [];
      const variables = matches.map((match: string) => match.replace(/\{\{|\}\}/g, '').trim());

      // Return unique variables only using a different approach to avoid TypeScript issues
      const uniqueVars: string[] = [];
      variables.forEach((v) => {
        if (!uniqueVars.includes(v)) {
          uniqueVars.push(v);
        }
      });
      return uniqueVars;
    };

    // Get variables from both HTML and text content
    const htmlVars = extractVariables(htmlContent);
    const textVars = extractVariables(form.getValues('textContent') || '');

    // Combine and set unique variables
    const allVars: string[] = [];
    [...htmlVars, ...textVars].forEach((v) => {
      if (!allVars.includes(v)) {
        allVars.push(v);
      }
    });
    if (allVars.length > 0) {
      form.setValue('variables', allVars);
    }
  }, [htmlContent, form]);

  // Create or update template mutation
  const saveTemplateMutation = useMutation({
    mutationFn: async (formData: TemplateFormValues) => {
      const url = isEditing ? `/api/email-templates/${id}` : '/api/email-templates';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await apiRequest({
        url: url,
        method: method,
        data: formData,
      });
      // apiRequest already returns the parsed JSON
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-templates'] });
      if (isEditing) {
        queryClient.invalidateQueries({ queryKey: ['/api/email-templates', id] });
      }

      toast({
        title: `Template ${isEditing ? 'updated' : 'created'} successfully`,
        description: `The email template has been ${isEditing ? 'updated' : 'created'}.`,
      });

      navigate('/email-templates');
    },
    onError: (error) => {
      toast({
        title: `Failed to ${isEditing ? 'update' : 'create'} template`,
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  // Handle form submission
  const onSubmit = (data: TemplateFormValues) => {
    // Ensure variables is an array
    const formData = {
      ...data,
      variables: data.variables || [],
    };

    saveTemplateMutation.mutate(formData);
  };

  // Handle variable updates
  const handleVariableChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const variablesText = e.target.value;
    const variablesArray = variablesText
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    form.setValue('variables', variablesArray);
  };

  if (isLoading && isEditing) {
    return <div className="container mx-auto p-6">Loading template data...</div>;
  }

  if (error && isEditing) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load template: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">{isEditing ? 'Edit' : 'Create'} Email Template</h1>
        <Button variant="outline" asChild>
          <Link href="/email-templates">Back to Templates</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isEditing ? 'Edit Existing' : 'Create New'} Email Template</CardTitle>
          <CardDescription>
            {isEditing
              ? 'Modify the content and settings for this email template.'
              : 'Create a new email template that can be used throughout the application.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Welcome Email" {...field} />
                      </FormControl>
                      <FormDescription>A unique name to identify this template</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template Type</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., notification, welcome, loa" {...field} />
                      </FormControl>
                      <FormDescription>Category for organizing templates</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Brief description of this template's purpose"
                        rows={2}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Explain when and how this template is used</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Subject</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Welcome to DomElec Solar" {...field} />
                    </FormControl>
                    <FormDescription>
                      The subject line for emails using this template
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="variables"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template Variables</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="customerName, signingUrl, installationDate"
                        rows={2}
                        value={(field.value || []).join(', ')}
                        onChange={handleVariableChange}
                      />
                    </FormControl>
                    <FormDescription>
                      Comma-separated list of variables that can be used in this template
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />

              <Tabs defaultValue="html" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="html">HTML Content</TabsTrigger>
                  <TabsTrigger value="text">Text Content</TabsTrigger>
                </TabsList>

                <TabsContent value="html" className="mt-4">
                  <FormField
                    control={form.control}
                    name="htmlContent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>HTML Version</FormLabel>
                        <FormControl>
                          <div className="border rounded-md overflow-hidden">
                            <ReactQuill
                              theme="snow"
                              modules={modules}
                              formats={formats}
                              value={htmlContent}
                              onChange={setHtmlContent}
                              placeholder="Create your email content here... Use {{variableName}} for dynamic content"
                            />
                          </div>
                        </FormControl>
                        <FormDescription>
                          Rich HTML content with variables in double curly braces
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>

                <TabsContent value="text" className="mt-4">
                  <FormField
                    control={form.control}
                    name="textContent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Plain Text Version</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Hello, {{customerName}}..."
                            rows={15}
                            className="font-mono text-sm"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Plain text content with variables in double curly braces
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>
              </Tabs>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/email-templates')}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saveTemplateMutation.isPending}>
                  {saveTemplateMutation.isPending
                    ? isEditing
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditing
                      ? 'Update Template'
                      : 'Create Template'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditEmailTemplate;
